/**
 * SpacesConfigMapper
 * Maps Spaces UI configurations (BuildSpace & CustomSpace) to ChatGame API format
 */

import { purposeFor, kindFor, seedFromQuestion, clampChars, QUESTION_MAX } from '../utils/spacePurpose';

class SpacesConfigMapper {

  // Role templates with descriptions and personality traits
  static ROLE_TEMPLATES = {
    'Professional Expert': {
      descriptionTemplate: (spec) => `Expert ${spec || 'professional'} with deep knowledge and experience in their field`,
      personality: ['Professional', 'Analytical', 'Detail-oriented'],
      objective: 'Provide expert advice and insights on topics being discussed'
    },
    'Project Manager/ Personal Assistant': {
      descriptionTemplate: () => 'Organized professional who helps structure tasks, manage priorities, and track progress',
      personality: ['Organized', 'Proactive', 'Supportive'],
      objective: 'Help structure work, set deadlines, and manage workload effectively'
    },
    'Teacher / Mentor': {
      descriptionTemplate: (spec) => `${spec || 'Educational'} teacher who breaks down complex topics into easy-to-understand chunks`,
      personality: ['Patient', 'Encouraging', 'Knowledgeable'],
      objective: 'Guide learning and provide personalized educational support'
    },
    'Certified Math & Science Nerd': {
      descriptionTemplate: () => 'Mathematics and science specialist with deep technical knowledge',
      personality: ['Analytical', 'Precise', 'Enthusiastic'],
      objective: 'Explain scientific and mathematical concepts clearly'
    },
    'Career Strategist': {
      descriptionTemplate: () => 'Professional development expert focused on career growth and strategy',
      personality: ['Strategic', 'Insightful', 'Motivating'],
      objective: 'Provide career guidance, interview prep, and professional development advice'
    },
    'Developer Sidekick / Tech Explorer': {
      descriptionTemplate: () => 'Programming expert who assists with code, debugging, and technical challenges',
      personality: ['Technical', 'Helpful', 'Problem-solving'],
      objective: 'Assist with programming, debugging, and exploring technical ideas'
    },
    'Laboratory Researcher': {
      descriptionTemplate: () => 'Scientific researcher skilled in experimentation, data analysis, and research methodology',
      personality: ['Methodical', 'Curious', 'Rigorous'],
      objective: 'Assist with research design, data analysis, and scientific experimentation'
    },
    'Visionary Artist / Designer': {
      descriptionTemplate: () => 'Creative designer with an eye for aesthetics, innovation, and artistic expression',
      personality: ['Creative', 'Bold', 'Artistic'],
      objective: 'Provide creative inspiration and design guidance'
    },
    'Brainstorm Buddy': {
      descriptionTemplate: () => 'Creative thinker who generates innovative ideas and explores possibilities',
      personality: ['Creative', 'Enthusiastic', 'Open-minded'],
      objective: 'Help generate and refine ideas through creative brainstorming'
    },
    'Devils Advocate': {
      descriptionTemplate: () => 'Critical thinker who challenges assumptions and tests ideas constructively',
      personality: ['Challenging', 'Analytical', 'Direct'],
      objective: 'Test and strengthen ideas by identifying weaknesses and alternatives'
    },
    'Gaming Partner': {
      descriptionTemplate: () => 'Playful companion who brings fun, engagement, and a gamified perspective',
      personality: ['Playful', 'Engaging', 'Fun'],
      objective: 'Provide entertainment, companionship, and playful interactions'
    },
    'Social Companion': {
      descriptionTemplate: () => 'Friendly conversationalist who is always ready to chat and provide companionship',
      personality: ['Friendly', 'Empathetic', 'Supportive'],
      objective: 'Provide companionship and engaging conversation'
    },
    'Wellness Buddy / Coach': {
      descriptionTemplate: () => 'Mindfulness and wellness expert focused on mental health and well-being',
      personality: ['Calm', 'Supportive', 'Reflective'],
      objective: 'Support mental health, mindfulness, and personal wellness'
    }
  };

  // 🎯 Goal → tool-flag presets (Nina 07-29). Only explicit false disables a
  // tool; missing keys mean ON. Kept deliberately coarse — four flags the
  // viewer/backend actually honor: arcade (Games tab), watch_party (host TV +
  // launcher emphasis), doodle (🖍️ canvas), polls (ambient host polls).
  // 📣 `live` (09-19): every tool on + crowd_inline. ⚠️ crowd_inline is the ONE
  // key here that is OPT-IN TRUE (the tool_flags.terminal precedent), not
  // "missing = ON": a missing key must keep the classic two-lane layout. The
  // viewer reads it through utils/crowdInline.isCrowdInline, never the goal
  // name — so moving the mode onto Hangout later is one line here and only
  // touches NEW spaces.
  static GOAL_PRESETS = {
    hangout: {},
    live: { crowd_inline: true },
    story: { arcade: false, watch_party: false },
    watch: { arcade: false, doodle: false },
    game: { watch_party: false },
    tea: { arcade: false, watch_party: false, doodle: false },
  };

  // Model name mapping — OFFLINE FALLBACK ONLY. The live truth is the backend's
  // src/ChatGame/modelRoster.js, served via /api?getModelRoster=true; CustomSpace
  // passes it in as customSpaceData.modelMap and it wins over this copy.
  // 🤖💸 10-01 agent floor (R10, Nina 10-01): everyday models only (premium is hidden until
  // premium users exist; the server turns any other id into Flash). 'Gemini 2.5 Pro',
  // 'ChatGPT (GPT-5.1)' and 'Qwen3 Max' left this offline copy.
  static MODEL_MAP = {
    'Gemini 3 Flash': 'google/gemini-3-flash-preview',
    'ChatGPT (GPT-5 Mini)': 'openai/gpt-5-mini',
    'Claude Haiku 4.5': 'anthropic/claude-haiku-4.5',
    // 🩹 Was 'Llama 3.3 Nemotron': 'nvidia/llama-3.3-nemotron-super-49b-v1.5'
    // — DEAD on OpenRouter (404 "No endpoints found") and replaced in the
    // backend roster on 07-31. This offline copy kept serving the dead id for
    // two weeks: exactly the drift the module docs warn about. Fixed 08-14.
    'Llama 4 Scout': 'meta-llama/llama-4-scout',
    'DeepSeek v3.2': 'deepseek/deepseek-v3.2',
    // Cheapest working seat (Nina 08-14). INSTRUCT build on purpose — the
    // qwen *flash* builds are hybrid-thinking and return empty/leaky messages
    // the way we call them. Full reasoning in modelRoster.js.
    'Qwen3 30B': 'qwen/qwen3-30b-a3b-instruct-2507',
    // Non-thinking K2, matching featuredCompetitions.js MODEL_MAP (user call 07-11:
    // the thinking variant burned 20-30s per turn). Keep the two maps in sync.
    'Kimi K2': 'moonshotai/kimi-k2-0905'
  };

  /**
   * Convert BuildSpace configuration to ChatGame config
   * @param {Object} buildSpaceData - BuildSpace form data
   * @returns {Object} ChatGame configuration
   */
  mapBuildSpaceToGameConfig(buildSpaceData) {
    const {
      spaceName,
      roles,
      expertDescription,
      mentorDescription,
      interests,
      otherInterest,
      isPublic,
      selectedPreset
    } = buildSpaceData;

    // Validate required fields
    if (!spaceName || !spaceName.trim()) {
      throw new Error('Space name is required');
    }

    if (!roles || roles.length === 0) {
      throw new Error('At least one role must be selected');
    }

    // Build interest list
    const allInterests = [...interests];
    if (otherInterest && otherInterest.trim()) {
      allInterests.push(otherInterest.trim());
    }

    // Generate objective from interests
    let objective = 'Have an interesting and engaging conversation';
    if (allInterests.length > 0) {
      objective = `Have an interesting conversation about ${allInterests.join(', ')}`;
    }

    // Generate scene description
    let sceneDescription = 'A group of characters gather in a comfortable space for discussion';
    if (allInterests.length > 0) {
      sceneDescription = `A group interested in ${allInterests.join(', ')} gathers to discuss and explore ideas`;
    }

    // Convert roles to player configs
    const playerConfigs = roles.map(role => {
      const template = SpacesConfigMapper.ROLE_TEMPLATES[role];
      if (!template) {
        throw new Error(`Unknown role: ${role}`);
      }

      // Get specialization for specific roles
      let specialization = null;
      if (role === 'Professional Expert' && expertDescription && expertDescription.trim()) {
        specialization = expertDescription.trim();
      } else if (role === 'Teacher / Mentor' && mentorDescription && mentorDescription.trim()) {
        specialization = mentorDescription.trim();
      }

      return {
        name: role,
        character_description: template.descriptionTemplate(specialization),
        personality_traits: template.personality.map(trait => ({ trait, percentage: 75 })),
        objective: template.objective,
        secret_info: '',
        is_human: false,
        model: 'google/gemini-3-flash-preview',  // Default model // 🤖💸 10-01 agent floor (R10): was sonnet-4.5
        role_specialization: specialization
      };
    });

    // Don't automatically add a human player - users will join via "Join Round" button
    // which creates a human player with name like "human_abc123"

    return {
      name: spaceName.trim(),
      objective,
      scene_description: sceneDescription,
      max_rounds: 5,  // Default
      max_messages_per_round: 20,  // Default
      players: playerConfigs  // Only AI players initially
    };
  }

  /**
   * Convert CustomSpace configuration to ChatGame config
   * @param {Object} customSpaceData - CustomSpace form data
   * @returns {Object} ChatGame configuration
   */
  mapCustomSpaceToGameConfig(customSpaceData) {
    const {
      spaceName,
      aiCounts,
      promptText,
      interests,
      otherInterest,
      showOther,
      personality,
      isPublic,
      safeModeEnabled,
      shareAIEnabled,
      fullImmersionEnabled,
      competitiveMode,
      competitiveObjective,
      academicProfilePic,
      academicGuidance,
      musicUrl,
      tagline,
      characterLooks,
      characterPersonas,
      artDirection,
      hasLinkedAgent
    } = customSpaceData;

    // Validate required fields
    if (!spaceName || !spaceName.trim()) {
      throw new Error('Space name is required');
    }

    // Check if at least one AI model is selected.
    // ⚠️ EXCEPT the marketplace lane: /custom-space?agentId= sets playerCount
    // to 0 on purpose (the linked agent is the whole cast), so zero model
    // seats is the CORRECT config there, not an empty form. CustomSpace's own
    // guard has carried that exemption since 92042c2d1; this one never got it,
    // which threw on every agent-linked launch.
    const totalAgents = Object.values(aiCounts).reduce((sum, count) => sum + count, 0);
    if (totalAgents === 0 && !hasLinkedAgent) {
      throw new Error('At least one AI model must be selected');
    }

    // Build interest list
    const allInterests = [...interests];
    if (showOther && otherInterest && otherInterest.trim()) {
      allInterests.push(otherInterest.trim());
    }

    // Use custom prompt or generate from interests
    let sceneDescription = promptText || 'A group gathers for discussion';
    let objective = 'Have an engaging conversation';

    if (allInterests.length > 0 && !promptText) {
      sceneDescription = `A group interested in ${allInterests.join(', ')} gathers for discussion`;
      objective = `Have an interesting conversation about ${allInterests.join(', ')}`;
    }

    // Override objective with competitive objective if provided
    if (competitiveMode && competitiveObjective) {
      objective = competitiveObjective;
    } else if (competitiveMode && promptText) {
      // Competitive mode on but no objective picked — use the prompt as the objective
      objective = promptText;
    }

    // Convert personality sliders to traits and scores
    const { traits } = this.convertPersonalityToTraits(personality);

    // Don't automatically add a human player - users will join via "Join Round" button
    // which creates a human player with name like "human_abc123"

    // Generate player configs from AI model counts
    const playerConfigs = [];  // Start with empty array

    // Live roster from the backend wins (getModelRoster endpoint); the static
    // MODEL_MAP below is the offline fallback only.
    const modelMap = customSpaceData.modelMap || SpacesConfigMapper.MODEL_MAP;
    Object.entries(aiCounts).forEach(([modelName, count]) => {
      if (count === 0) return;

      const modelString = modelMap[modelName];
      if (!modelString) {
        console.warn(`Unknown model: ${modelName}, skipping`);
        return;
      }

      for (let i = 0; i < count; i++) {
        const playerName = count > 1 ? `${modelName} ${i + 1}` : modelName;

        // characterLooks is a flat array aligned to seat order (same
        // Object.entries(aiCounts) iteration as this loop — built in CustomSpace).
        const seatLook = Array.isArray(characterLooks) ? (characterLooks[playerConfigs.length] || '').trim() : '';
        // 08-13 (Nina D7): owner-written persona wins the seat's description.
        // ⚠️ When absent the promptText copy MUST stay byte-identical — the
        // post-create designer-LLM's isCopiedSeed equality depends on it
        // (never wrap/prefix promptText here; NONCOMP_CRISPING_SPEC §3.1 trap).
        const seatPersona = Array.isArray(characterPersonas) ? (characterPersonas[playerConfigs.length] || '').trim() : '';

        playerConfigs.push({
          name: playerName,
          character_description: seatPersona || promptText || `An AI assistant participating in the conversation`,
          // Explicit provenance beats string inequality: if the owner pastes
          // the vibe text verbatim as a persona, isCopiedSeed would call it a
          // copy and overwrite it — this flag makes owner text inviolable.
          ...(seatPersona && { owner_written_persona: true }),
          ...(seatLook && { visual_description: seatLook }),
          personality_traits: traits,
          objective: objective,
          secret_info: '',
          is_human: false,
          model: modelString
        });
      }
    });

    if (playerConfigs.length === 0 && !hasLinkedAgent) {
      // No AI models selected. Same marketplace exemption as the count check
      // above — an agent-linked space legitimately builds zero model seats and
      // gets its cast from the /invite call CustomSpace fires after create.
      throw new Error('Please select at least one AI model to participate in the conversation');
    }

    // Imposter mode: include human player upfront so they can be randomly assigned as the Glitch
    if (customSpaceData.imposterMode) {
      playerConfigs.push({
        name: 'You',
        character_description: 'A human competitor',
        personality_traits: [],
        objective: objective,
        secret_info: '',
        is_human: true,
        model: null
      });
    }

    return {
      name: spaceName.trim(),
      objective,
      scene_description: sceneDescription,
      max_rounds: 5,  // Default
      max_messages_per_round: 20,  // Default
      players: playerConfigs,
      academic_profile_pic: academicProfilePic || null,
      academic_guidance: academicGuidance || null,
      is_shared: isPublic || false,
      interests: allInterests,
      // Competitive mode configuration
      competitive_mode: competitiveMode || false,
      messages_per_round: 4, // Hardcoded for now
      total_rounds: 3, // Hardcoded for now
      // Imposter (Glitch) mode
      imposter_mode: customSpaceData.imposterMode || false,
      imposter_word_rounds: 3,
      // Author
      author: customSpaceData.author?.trim() || null,
      // Custom music
      music_url: musicUrl?.trim() || null,
      // 📺 Space TV — ambient host-run watch party. Undefined/true = on (fleet
      // default); explicit false turns the host TV off for this space. A goal
      // preset with watch_party off wins over an UNTOUCHED default-on switch.
      space_tv_enabled: customSpaceData.spaceTvEnabled !== false
        && !(!customSpaceData.spaceTvTouched
             && (SpacesConfigMapper.GOAL_PRESETS[customSpaceData.spaceGoal] || {}).watch_party === false),
      // Builder-curated "what's on" — YouTube URLs, one per line/comma; the host
      // plays these before falling back to AI-picked media suggestions.
      space_tv_seed_urls: String(customSpaceData.spaceTvSeeds || '')
        .split(/[\n,]+/)
        .map(s => s.trim())
        .filter(s => /^https?:\/\/(www\.)?(youtube\.com|youtu\.be)\//.test(s))
        .slice(0, 10),
      // 🎯 Space goal → preset tool loadout (Nina 07-29). Additive gates:
      // a missing key means ON — only an explicit false disables a tool, so
      // presets tune defaults without ever removing capabilities.
      space_goal: customSpaceData.spaceGoal || 'hangout',
      tool_flags: SpacesConfigMapper.GOAL_PRESETS[customSpaceData.spaceGoal] || {},
      // Tagline
      tagline: tagline?.trim() || null,
      // Cast portrait art direction (space-level, optional)
      art_direction: artDirection?.trim() || null
    };
  }

  /**
   * 🧱 Build form v2 (pages/BuildSpaceV2.js, SPACES_REDESIGN_BRAINSTORM §4):
   * the PURPOSE path. Additive: mapCustomSpaceToGameConfig above is untouched
   * (spacesConfigMapper.purpose.test.js pins its output byte for byte), and
   * this builds on it so both forms send one body shape.
   *
   * What differs from the old form:
   *   · `purpose` / `purpose_kind` ride the body (the backend keeps only
   *     valid values: justsomeguy-backend/src/ChatGame/spacePurpose.js)
   *   · 💳 10-28 (Nina: Chip in in "Every room"): Chip in is ON unless the
   *     maker switched it off; then, and only then, `chip_in_off: true` rides
   *     along. The old `chip_in` key is never sent (it did nothing, and every
   *     room the form made before stored false by default).
   *   · space_goal + tool_flags come from the sub-pick (utils/spacePurpose.js)
   *   · the question goes VERBATIM into `objective` (1000 cap), and ONE ≤500
   *     sentence-safe cut of it is BOTH scene_description and every local's
   *     seed, byte for byte (isCopiedSeed swaps a seed for a real bio only
   *     when it equals the scene or the objective; a 500+ char seed would
   *     400 the whole create). A blank question uses the sub-pick's scene line.
   *   · no sliders, looks or personas (the seats' traits stay empty)
   * @param {Object} data - { spaceName, purpose, purposeKind, question,
   *   isListed, chipIn, aiCounts, modelMap, hasLinkedAgent, musicUrl,
   *   spaceTvEnabled, interests, tagline, artDirection }
   * @returns {Object} ChatGame configuration
   */
  mapPurposeSpaceToGameConfig(data) {
    const purpose = purposeFor(data && data.purpose);
    if (!purpose) throw new Error('Pick what the space is for');
    const kind = kindFor(purpose.key, data.purposeKind);
    const question = clampChars(String(data.question || '').trim(), QUESTION_MAX).trim();
    const seed = seedFromQuestion(question) || kind.scene;

    const base = this.mapCustomSpaceToGameConfig({
      spaceName: data.spaceName,
      aiCounts: data.aiCounts || {},
      promptText: seed,
      interests: Array.isArray(data.interests) ? data.interests : [],
      otherInterest: '',
      showOther: false,
      personality: {},
      isPublic: data.isListed !== false,
      competitiveMode: false,
      competitiveObjective: null,
      imposterMode: false,
      author: '',
      musicUrl: data.musicUrl || '',
      spaceGoal: kind.goal,
      hasLinkedAgent: !!data.hasLinkedAgent,
      modelMap: data.modelMap || null,
      tagline: data.tagline || '',
      characterLooks: [],
      characterPersonas: [],
      artDirection: data.artDirection || '',
    });

    // Space TV is the builder's switch (its default follows the sub-pick). A
    // room that turns the TV ON gets the host's video picks back (media), or
    // its TV would have nothing to play.
    const spaceTvEnabled = typeof data.spaceTvEnabled === 'boolean' ? data.spaceTvEnabled : kind.flags.watch_party !== false;
    const toolFlags = { ...kind.flags };
    if (spaceTvEnabled && toolFlags.media === false) delete toolFlags.media;

    const { academic_profile_pic, academic_guidance, ...rest } = base; // dropped server-side anyway
    return {
      ...rest,
      objective: question || kind.scene,
      space_tv_enabled: spaceTvEnabled,
      space_goal: kind.goal,
      tool_flags: toolFlags,
      purpose: purpose.key,
      purpose_kind: kind.key,
      ...(data.chipIn === false ? { chip_in_off: true } : {}),
    };
  }

  /**
   * Convert personality sliders (0-100) to trait strings and scores
   * @param {Object} personality - Personality slider values
   * @returns {Object} { traits: string[], scores: Object }
   */
  convertPersonalityToTraits(personality) {
    const traits = [];
    const scores = {};

    const traitMap = {
      friendlyFormal: { left: 'Sassy', right: 'Serious' },
      conciseVerbose: { left: 'Concise', right: 'Verbose' },
      playfulSerious: { left: 'Lighthearted', right: 'Strategic' },
      empatheticObjective: { left: 'Empathetic', right: 'Objective' },
      supportiveChallenging: { left: 'Supportive', right: 'Challenging' },
      curiousReserved: { left: 'Curious', right: 'Reserved' },
      lightheartedPhilosophical: { left: 'Lighthearted', right: 'Philosophical' },
      optimisticRealistic: { left: 'Optimistic', right: 'Realistic' }
    };

    Object.entries(personality || {}).forEach(([key, value]) => {
      scores[key] = value;

      if (traitMap[key]) {
        if (value < 40) {
          // Left-leaning trait: percentage is inverse of value
          const percentage = Math.round((40 - value) / 40 * 100);
          traits.push({ trait: traitMap[key].left, percentage });
        } else if (value > 60) {
          // Right-leaning trait: percentage is how much over 60
          const percentage = Math.round((value - 60) / 40 * 100);
          traits.push({ trait: traitMap[key].right, percentage });
        }
        // Balanced (40-60) traits not explicitly added to keep array concise
      }
    });

    return { traits, scores };
  }
}

export default SpacesConfigMapper;
